import * as Stimulus from "@hotwired/stimulus"
/** @import { ActionEvent } from "@hotwired/stimulus"} **/

/**
 * @class AWSPresignedPostController
 * @classdesc Stimulus controller to handle direct file uploads to AWS S3 using presigned POST URLs.
 * This controller intercepts a form submission, uploads the selected file via XMLHttpRequest
 * to show progress, and displays success or error messages using HTML templates.
 *
 * @property {HTMLTemplateElement} successTemplateTarget - Required. Template used to display success message after upload.
 * @property {HTMLTemplateElement} progressTemplateTarget - Required. Template used to display upload progress. Must contain a <progress> element.
 * @property {HTMLTemplateElement} errorTemplateTarget - Optional. Template used to display an error message if the upload fails.
 * @property {HTMLElement} outputTarget - Required. Container element where progress, success, or error templates will be rendered.
 *
 * @example Basic HTML Structure:
 * <div data-controller="aws-presigned-post">
 * <form action="YOUR_PRESIGNED_URL_HERE" method="post" enctype="multipart/form-data" data-action="submit->aws-presigned-post#submit">
 * * <input type="hidden" name="key" value="uploads/${filename}">
 * <input type="hidden" name="AWSAccessKeyId" value="YOUR_ACCESS_KEY_ID">
 * <input type="hidden" name="policy" value="YOUR_POLICY_DOCUMENT_BASE64">
 * <input type="hidden" name="signature" value="YOUR_SIGNATURE">
 * <input type="hidden" name="Content-Type" value="YOUR_CONTENT_TYPE">
 * <input type="hidden" name="success_action_status" value="201"> * *
 * <input type="file" name="file" required>
 *
 * <button type="submit">Upload</button>
 * </form>
 *
 * * <div data-aws-presigned-post-target="output"></div>
 *
 * * <template data-aws-presigned-post-target="progressTemplate">
 * <div>
 * <p>Uploading {{fileName}} ({{fileSize|formatted}})...</p>
 * <progress value="0" max="100"></progress>
 * </div>
 * </template>
 *
 * <template data-aws-presigned-post-target="successTemplate">
 * <div>
 * <p>Successfully uploaded {{fileName}} to {{Location}}.</p>
 * <p>Size: {{fileSize|formatted}}, Type: {{mimeType}}, ETag: {{ETag}}, Key: {{Key}}</p>
 * </div>
 * </template>
 *
 * <template data-aws-presigned-post-target="errorTemplate">
 * <div>
 * <p>Error uploading {{fileName}}:</p>
 * <p>{{Message}}</p> * </div>
 * </template>
 * </div>
 *
 * @description How Templating Works:
 * The controller uses simple string replacement within the templates. Use double curly braces `{{key}}`
 * to insert values.
 *
 * Available variables in ALL templates (progress, success, error):
 * - {{fileName}}: The name of the uploaded file.
 * - {{fileSize}}: The size of the file in bytes (e.g., 1048576).
 * - {{fileSize|formatted}}: The human-readable file size (e.g., "1 MB").
 * - {{lastModified}}: The file's last modified date, formatted according to browser locale.
 * - {{mimeType}}: The MIME type of the file (e.g., "image/jpeg").
 *
 * Available variables ONLY in the successTemplate:
 * (These come from the XML response S3 sends on status 201)
 * - {{Location}}: The full URL of the uploaded object.
 * - {{Bucket}}: The name of the S3 bucket.
 * - {{Key}}: The key (path) of the object within the bucket.
 * - {{ETag}}: The ETag (entity tag) of the uploaded object, often an MD5 hash.
 *
 * Available variables ONLY in the errorTemplate:
 * - {{Message}}: The error message. If S3 returns an XML error document, the content of the `<Message>` tag is used.
 * Otherwise, it defaults to "Unexpected error".
 *
 * Server-Side Setup:
 * Your server must generate an S3 presigned POST URL and the associated hidden form fields.
 * **Crucially**, you MUST include a field named `success_action_status` with a value of `201`.
 * This tells S3 to return an XML document with details upon successful upload, which this controller uses.
 * If `success_action_status` is not `201`, the controller will allow the default form submission (likely a redirect).
 */
export class AWSPresignedPostController extends Stimulus.Controller {
    static get targets() {
        return [
            "successTemplate",
            "progressTemplate",
            "errorTemplate", // Optional
            "output",
        ];
    }

    initialize() {
        this.locale = (navigator.languages && navigator.languages[0]) || 'en-US';
    }

    connect() {
        if (!this.hasOutputTarget) {
            console.error(`Missing required target: '[data-${this.identifier}-target="output"]'. This element is needed to display upload status.`);
        }
        if (!this.hasProgressTemplateTarget) {
            console.error(`Missing required target: '[data-${this.identifier}-target="progressTemplate"]'. This <template> is needed to show upload progress.`);
        }
        if (!this.hasSuccessTemplateTarget) {
            console.error(`Missing required target: '[data-${this.identifier}-target="successTemplate"]'. This <template> is needed to show upload success messages.`);
        }
        // errorTemplateTarget is optional, checked inline where used.
    }

    requestSubmit(e) {
        e.target?.form?.requestSubmit();
    }

    /**
     * Handles the form submission event.
     * Intercepts the submission if success_action_status is 201,
     * initiates the direct S3 upload, and displays progress.
     * @param {SubmitEvent} e - The form submission event.
     */
    submit(e) {
        // Create FormData based on the submitted form and button
        // Using e.submitter ensures correct button value is included if needed, though not typical for S3 POST
        let formData = new FormData(e.target, e.submitter);

        // IMPORTANT: Only intercept if success_action_status is '201'.
        // Otherwise, let the browser handle the submission (e.g., a redirect configured in the presigned post).
        if (formData.get('success_action_status') !== '201') {
            console.warn(`[${this.identifier}] 'success_action_status' field is not '201'. Allowing default form submission.`);
            return;
        }

        // Prevent the default form submission and stop propagation
        e.preventDefault();
        e.stopPropagation();

        function cloneFormData(formData) {
            const res = new FormData();
            for (const [k, v] of formData.entries()) {
                res.append(k, v)
            }
            return res
        }
        const files = formData.getAll('file');
        if (files.length == 1) {
            this.uploadFile(e.target.action, formData);
        } else {
            for (const file of files) {
                const fileFormData = cloneFormData(formData);
                fileFormData.set('file', file);
                this.uploadFile(e.target.action, fileFormData)
            }
        }

        // Reset the form fields after initiating the upload
        e.target.reset();
    }

    uploadFile(url, formData) {
        const file = formData.get('file');
        if (file.size === 0) {
            console.warn(`[${this.identifier}] File is empty.`);
            // Optionally show an error message here using the error template
            return;
        }

        // Clone the progress template
        let progressTemplateContent = this.progressTemplateTarget.content.cloneNode(true);
        // Get the root element *within* the cloned template content
        let progressRoot = progressTemplateContent.firstElementChild;

        if (!progressRoot) {
            console.error(`[${this.identifier}] progressTemplate is empty or invalid.`);
            return;
        }

        // Populate the progress template with initial file data
        this.applyValuesToTemplate(progressRoot, this.fileToValues(file));
        // Append the populated progress element to the output area
        this.outputTarget.appendChild(progressRoot); // Use progressRoot, not progressTemplateContent

        // Start the upload process
        this.sendPostRequest(url, formData, progressRoot);
    }

    abort(e) {
        this.dispatch('abort', { details: e?.params, target: e?.target || this.element });
    }

    /**
     * Sends the file data to the S3 presigned URL using XMLHttpRequest.
     * Handles progress, success, and error states.
     * @param {string} url - The S3 presigned POST URL.
     * @param {FormData} formData - The form data containing the file and S3 fields.
     * @param {HTMLElement} progressElement - The DOM element representing the progress UI for this upload.
     */
    sendPostRequest(url, formData, progressElement) {
        let request = new XMLHttpRequest();
        request.open("POST", url);

        progressElement.addEventListener(`${this.identifier}:abort`, () => {
            request.abort();
        });
        // Find the <progress> element within the specific progress display element
        let progressBar = progressElement.querySelector('progress');
        if (!progressBar) {
            console.warn(`[${this.identifier}] No <progress> element found within the progressTemplate instance.`);
        }

        // Update progress bar during upload
        request.upload.onprogress = (e) => {
            if (e.lengthComputable && progressBar) {
                progressBar.max = e.total;
                progressBar.value = e.loaded;
            }
        };

        // Handle successful upload (HTTP status 2xx/3xx)
        request.onload = () => {
            let templateContent;
            let status = request.status;

            if (status >= 200 && status < 300) { // Typically 201 for our setup
                if (status !== 201) {
                    console.warn(`[${this.identifier}] Received status ${status} instead of 201. S3 might not have returned the expected XML response.`);
                }
                templateContent = this.successTemplateTarget.content.cloneNode(true);
            } else if (status >= 400 && this.hasErrorTemplateTarget) {
                templateContent = this.errorTemplateTarget.content.cloneNode(true);
            }

            if (templateContent) {
                let responseDoc = request.responseXML;
                let responseValues = {};
                // Try to parse S3's XML response (expected for 201, possible for errors)
                if (responseDoc) {
                    responseValues = this.postResponseToValues(responseDoc);
                } else if (status >= 400) {
                    responseValues['Message'] = 'Unexpected Error';
                }

                let allValues = Object.assign({}, responseValues, this.fileToValues(formData.get('file')));
                this.applyValuesToTemplate(templateContent, allValues);
                // Replace the progress indicator with the success/error message
                this.outputTarget.replaceChild(templateContent, progressElement);
            } else {
                // If no template is available for the status (e.g., error occurred but no error template)
                // just remove the progress indicator.
                this.outputTarget.removeChild(progressElement);
            }
        };

        // Handle network errors
        request.onerror = () => {
            if (!this.hasErrorTemplateTarget) {
                this.outputTarget.removeChild(progressElement); // Clean up progress
                console.error(`[${this.identifier}] Network error during upload, and no error template provided.`);
                return;
            }

            let templateContent = this.errorTemplateTarget.content.cloneNode(true);
            if (!templateContent) {
                console.error(`[${this.identifier}] Error template is empty or invalid.`);
                this.outputTarget.removeChild(progressElement); // Clean up progress indicator
                return;
            }

            let values = Object.assign({ 'Message': 'Network error during upload.' }, this.fileToValues(formData.get('file')));
            this.applyValuesToTemplate(templateContent, values);
            this.outputTarget.replaceChild(templateContent, progressElement);
        };

        // Handle upload cancellation
        request.onabort = () => {
            this.outputTarget.removeChild(progressElement);
            console.log(`[${this.identifier}] Upload aborted.`);
        };

        // Handle upload timeout
        request.ontimeout = () => {
            this.outputTarget.removeChild(progressElement);
            console.error(`[${this.identifier}] Upload timed out.`);
            // Optionally show error template here as well
        };

        // Initiate the request
        request.send(formData);
    }

    /**
     * Extracts relevant information from a File object.
     * @param {File} file - The file object.
     * @returns {object} An object containing file metadata.
     */
    fileToValues(file) {
        // Return dynamic functions for formatted values to avoid calculating unless needed
        return {
            'fileName': file.name,
            'fileSize': file.size,
            'fileSize|formatted': () => this.formatBytes(file.size),
            'lastModified': () => file.lastModifiedDate ? file.lastModifiedDate.toLocaleDateString(this.locale, {
                year: "numeric",
                month: "long",
                day: "numeric",
                hour: "numeric",
                minute: "numeric",
                timeZoneName: "short"
            }) : 'N/A',
            'mimeType': file.type || 'Unknown', // Fallback if type is unknown
        };
    }

    /**
      * Formats bytes into a human-readable string (KB, MB, GB).
      * @param {number} bytes - The number of bytes.
      * @returns {string} The formatted string.
      */
    formatBytes(bytes) {
        const units = ['byte', 'kilobyte', 'megabyte', 'gigabyte']

        let unitIndex = 0
        let value = bytes;
        while (value > 1024 && unitIndex < units.length) {
            value = value / 1024
            unitIndex++;
        }
        let unit = units[unitIndex];
        return Intl.NumberFormat(this.locale, {
            style: 'unit',
            unit,
            maximumFractionDigits: 2,
        }).format(value);
    }


    /**
     * Parses the XML response from S3 (expected on 201 status)
     * and extracts key elements like Location, Bucket, Key, ETag.
     * Also handles S3 error XML structure (<Error><Message>...</Message></Error>).
     * @param {Document} document - The parsed XML document from the response.
     * @returns {object} An object containing extracted values.
     */
    postResponseToValues(document) {
        if (!document || !document.documentElement) return {}; // Guard against null/invalid XML

        const rootElement = document.documentElement;
        let values = {};
        for (const child of rootElement.children) {
            values[child.tagName] = child.textContent;
        }
        return values;
    }

    /**
     * Traverses a DOM node (typically cloned from a template) and replaces
     * placeholders `{{key}}` in text content and attribute values.
     * @param {Node} node - The root node of the structure to process (e.g., the first element child of a cloned template).
     * @param {object} values - An object where keys match the placeholders. Values can be strings or numbers.
     */
    applyValuesToTemplate(node, values) {
        // Stack for depth-first traversal, starting with the root node itself
        let nodeStack = [node];

        while (nodeStack.length > 0) {
            let currentNode = nodeStack.shift(); // Process current node

            // 1. Process Attributes
            if (currentNode.attributes) {
                for (const attr of currentNode.attributes) {
                    if (attr.value.includes('{{')) {
                        attr.value = this.applyValuesToString(attr.value, values);
                    }
                }
            }

            if (currentNode.childNodes && currentNode.childNodes.length > 0) {
                for (const child of currentNode.childNodes) {
                    nodeStack.unshift(child);
                }
            } else {
                if (currentNode.textContent.includes('{{')) {
                    currentNode.textContent = this.applyValuesToString(currentNode.textContent, values);
                }
            }
        }
    }

    /**
     * Replaces placeholders `{{key}}` in a string with values from the provided object.
     * @param {string} str - The input string possibly containing placeholders.
     * @param {object} values - The key-value pairs for replacement.
     * @returns {string} The string with placeholders replaced.
     */
    applyValuesToString(str, values) {
        let res = str;
        for (const [key, value] of Object.entries(values)) {
            res = res.replaceAll("{{" + key + "}}", value);
        }
        return res;
    }
}
